import { useParams } from "react-router-dom";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { apiRoot } from "../../../index";
import { convertBookingObj } from "../../../store/actions/bookingActions";
import "moment/locale/fr";
import styled from "styled-components";
import { Button, Dropdown, Form, Modal } from "react-bootstrap";
import Select from "react-select";
import { _defaultBooking } from "../../../utils/booking";
import { startLoading, stopLoading } from "../../../utils/loader";
import DotsIcon from "../../icons/DotsIcon";

// region Select driver modal

function UpdateDriverModal({
  driverOptions,
  show,
  setShow,
  booking,
  submit,
  fetching,
}) {
  const driverSelect = useRef(null);
  const [driver, setDriver] = useState(booking?.driver);

  // update driver when booking.driver changes
  useEffect(() => {
    setDriver(booking?.driver);
  }, [booking?.driver]);

  return (
    <Modal show={show} onHide={() => setShow(false)}>
      <Modal.Header closeButton>
        <Modal.Title>Modifier le chauffeur</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group controlId="driver">
            <Form.Label>Chauffeur</Form.Label>
            <Select
              ref={driverSelect}
              options={driverOptions}
              value={
                driver?.id
                  ? { value: driver.id, label: driver.name, data: driver }
                  : null
              }
              onChange={(obj) => {
                setDriver(obj?.data || _defaultBooking.driver);
              }}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={() => setShow(false)}
          disabled={fetching}
        >
          Fermer
        </Button>
        <Button
          variant="primary"
          onClick={(e) => {
            e.preventDefault();
            submit(driver);
          }}
          disabled={fetching}
        >
          {fetching ? "Enregistrement..." : "Enregistrer les modifications"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// endregion

export const bookingProgressStatusList = [
  "Aucun",
  "En route",
  "Sur place",
  "Passagers à bord",
  "Mission cloturée",
];

export const bookingProgressStatusActionList = [
  "Je suis en route",
  "Je suis sur place",
  "Les passagers sont à bord",
  "Passagers déposés",
  "Mission cloturée",
];

function TrackContent() {
  // region states

  const [bookingRaw, setBookingRaw] = useState(null);
  const [comingBookingRaw, setComingBookingRaw] = useState([]);

  const [comment, setComment] = useState("");
  const [currentPos, setCurrentPos] = useState([0, 0]);
  const [currentPosInit, setCurrentPosInit] = useState(false);
  const [locationApiError, setLocationApiError] = useState(null);
  const [updating, setUpdating] = useState(true);
  const _isUpdating = useRef(false);
  const watchPosId = useRef(null);
  const params = useParams();

  const booking = useMemo(() => {
    if (bookingRaw) {
      return convertBookingObj(bookingRaw);
    }
    return {};
  }, [bookingRaw]);

  const isLaPremierePickup = useMemo(() => {
    return (
      /La Premiere/i.test(booking.firstname + " " + booking.lastname) &&
      (/CDG/.test(booking.pickup_address) ||
        /A[eé]roport.*Charles.*de.*Gaulle/i.test(booking.pickup_address))
    );
  }, [booking]);
  const isLaPremiereDropoff = useMemo(() => {
    return (
      /La Premiere/i.test(booking.firstname + " " + booking.lastname) &&
      (/CDG/.test(booking.dropoff_address) ||
        /A[eé]roport.*Charles.*de.*Gaulle/i.test(booking.dropoff_address))
    );
  }, [booking]);

  const comingBookings = useMemo(() => {
    return comingBookingRaw.map((b) => convertBookingObj(b));
  }, [comingBookingRaw]);

  const refreshBooking = useCallback(() => {
    setUpdating(true);
    _isUpdating.current = true;

    axios
      .get(
        apiRoot + "/api/v1/driverapp/track/" + params.uid + "/" + params.token
      )
      .then((res) => {
        const { data } = res;

        setBookingRaw(data);
        setUpdating(false);
        _isUpdating.current = false;
      });
  }, [params.token, params.uid]);

  // endregion

  // region useEffects

  // watch current Position
  useEffect(() => {
    if (!booking || !booking.driverJwt) return;
    if (watchPosId.current) {
      navigator.geolocation.clearWatch(watchPosId.current);
      watchPosId.current = null;
    }
    watchPosId.current = navigator.geolocation.watchPosition(
      (pos) => {
        const newPos = [pos.coords.latitude, pos.coords.longitude];
        setCurrentPos(newPos);
        setCurrentPosInit(true);
      },
      (e) => {
        setLocationApiError(e.code);
      },
      { enableHighAccuracy: true }
    );
  }, [booking, refreshBooking]);

  // Init booking datas
  useEffect(() => {
    axios
      .get(
        apiRoot + "/api/v1/driverapp/track/" + params.uid + "/" + params.token
      )
      .then((res) => {
        const { data } = res;

        setBookingRaw(data);
        setUpdating(false);
        if (data.driverJwt) {
          setComment(data.driver_comment || "");
          axios
            .get(
              apiRoot +
                "/api/v1/driverapp/track/coming/" +
                params.uid +
                "/" +
                params.token
            )
            .then((res) => {
              const { data } = res;
              setComingBookingRaw(data);
            });
        }
      });
  }, [params]);

  // endregion

  // region company user can update driver

  const [availableDrivers, setAvailableDrivers] = useState(null);
  const [showUpdateDriverModal, setShowUpdateDriverModal] = useState(false);
  const [fetchingUpdateDriver, setFetchingUpdateDriver] = useState(false);

  // Init available drivers (if logged in as current driver's company)
  useEffect(() => {
    if (!booking?.driverJwt) return;

    axios
      .get(
        apiRoot +
          `/api/v1/driverapp/track/availableDrivers/${params.uid}/${params.token}`
      )
      .then((res) => {
        if (res.data && res.data.length) {
          let options = res.data.map((d) => ({
            value: d.id,
            label: d.name,
            data: d,
          }));
          setAvailableDrivers(options);
        }
      });
  }, [params.token, params.uid, booking]);

  async function updateDriver(driver) {
    setFetchingUpdateDriver(true);
    startLoading();

    axios
      .post(
        apiRoot +
          "/api/v1/driverapp/track/updateDriver/" +
          params.uid +
          "/" +
          params.token,
        {
          driver,
        }
      )
      .then((res) => {
        setFetchingUpdateDriver(false);
        stopLoading();
        document.location.href = res.data;
      })
      .catch((e) => {
        setFetchingUpdateDriver(false);
        stopLoading();
        console.error("Error while updating driver", e);
        window.alert(
          "Une erreur est survenue lors de la mise à jour du chauffeur"
        );
        document.location.reload();
      });
  }

  // endregion

  // region handlers
  function updateStopList() {
    setUpdating(true);
    if (
      !_isUpdating.current &&
      window.confirm("Voulez vous signaler un arrêt non prévu ?")
    ) {
      _isUpdating.current = true;
      axios
        .post(
          apiRoot +
            "/api/v1/driverapp/track/updateStop/" +
            params.uid +
            "/" +
            params.token,
          {
            currentPos,
          }
        )
        .then(() => {
          window.alert("L'arrêt a bien été enregistré.");
          setUpdating(false);
          _isUpdating.current = false;
        });
    } else {
      setUpdating(false);
    }
  }

  function updateNoShow() {
    setUpdating(true);
    if (
      !_isUpdating.current &&
      window.confirm('Voulez vous signaler un "No Show" ?')
    ) {
      _isUpdating.current = true;
      axios
        .post(
          apiRoot +
            "/api/v1/driverapp/track/updateNoShow/" +
            params.uid +
            "/" +
            params.token,
          {
            currentPos,
          }
        )
        .then(() => {
          window.alert('Le "No Show" a bien été enregistré.');
          refreshBooking();
        });
    } else {
      setUpdating(false);
    }
  }

  function updateStatus() {
    setUpdating(true);
    if (
      !_isUpdating.current &&
      (bookingRaw.progress_status || 0) < 4 &&
      window.confirm(
        `Voulez vous passer le statut à "${
          bookingProgressStatusList[(bookingRaw.progress_status || 0) + 1]
        }" ?`
      )
    ) {
      _isUpdating.current = true;
      axios
        .post(
          apiRoot +
            "/api/v1/driverapp/track/updateStatus/" +
            params.uid +
            "/" +
            params.token,
          { currentPos }
        )
        .then(refreshBooking);
    } else {
      setUpdating(false);
    }
  }

  function driverLeave() {
    if (
      window.confirm(
        "Vous confirmez être dans l'incapacité à faire cette course ?"
      )
    ) {
      axios
        .get(
          apiRoot +
            "/api/v1/driverapp/track/driverleave/" +
            params.uid +
            "/" +
            params.token
        )
        .then(() => {
          window.location.href = "/";
        });
    }
  }

  function submitComment(e) {
    e.preventDefault();
    axios
      .post(
        apiRoot +
          "/api/v1/driverapp/track/updatecomment/" +
          params.uid +
          "/" +
          params.token,
        {
          text: comment,
        }
      )
      .then(() => {
        setBookingRaw((old) => ({
          ...old,
          driver_comment: comment,
        }));
        window.alert("Le commentaire à bien été enregistré");
      });
  }

  // endregion

  return (
    <div>
      {bookingRaw ? (
        <>
          {booking.driverJwt && !currentPosInit && (
            <Content>
              <h3>Attention</h3>
              <span className="text-white bg-danger">
                Votre geocalisation n’est pas activée , merci de l’activer sinon
                l’attente ne sera pas facturable
              </span>
              {locationApiError === 1 && (
                <p className="text-danger mt-2">
                  Vous avez refusé l'accès du navigateur à votre position.
                  Veuillez mettre à jour vos préférences dans les paramètres du
                  navigateur.
                </p>
              )}
              <div className="text-muted mt-2">
                Pour activer la géolocalisation :
                <details open>
                  <summary>iPhone:</summary>
                  <pre className="mt-1 mb-1">
                    Réglages -{">"} Confidentialité -{">"} Service de
                    localisation
                  </pre>
                  Si besoin, autoriser votre navigateur dans la liste des
                  application sur le même écran de réglage
                </details>
                <details open className="mt-2">
                  <summary>Android:</summary>
                  <pre>
                    Paramètres -{">"} Sécurité et Confidentialité -{"> "}
                    Localiser mon appareil
                  </pre>
                </details>
                <span>
                  Après tout changement de réglages, actualisez la page
                </span>
              </div>
            </Content>
          )}
          <Content>
            <h3>
              {booking?.driverJwt
                ? "Informations client"
                : "Informations chauffeur"}
            </h3>
            {!booking?.driverJwt && !booking.driver ? (
              "Les données du chauffeur arrivent bientôt."
            ) : (
              <ItemsGrid>
                <div>Nom :</div>
                <div>
                  {booking.driverJwt
                    ? booking.firstname.trim() + " " + booking.lastname
                    : booking.driver}
                </div>
                <div>Téléphone :</div>
                <div>
                  {booking.driverJwt
                    ? booking.phone || "N/A"
                    : booking.driver_phone || "N/A"}
                </div>
              </ItemsGrid>
            )}
          </Content>
          <Content>
            <h3>Informations sur la course</h3>
            <ItemsGrid>
              <div>Date de départ :</div>
              <div>
                {booking.pickup_date.dateMoment
                  .locale("fr-FR")
                  .format("dddd DD MMM YYYY")}{" "}
                à{" "}
                <strong>
                  {booking.pickup_date.dateMoment.format("HH:mm")}
                </strong>
              </div>
              <div>Adresse de départ :</div>
              <div>
                {booking.pickup_address}
                {booking.driverJwt && isLaPremierePickup && (
                  <>
                    <br />
                    <strong>
                      Arrivée au Terminal 2E : prise en charge porte 17, parvis
                      rouge – Air France La Première. Si le vol atterrit à un
                      autre terminal (rare), rendez-vous à l’accueil tablette.
                    </strong>
                  </>
                )}
              </div>
              <div>Adresse d'arrivée :</div>
              <div>
                {booking.dropoff_address}
                {booking.driverJwt && isLaPremiereDropoff && (
                  <>
                    <br />
                    <strong>
                      Pour les départ depuis le Terminal 2E, dépose à la porte
                      17 sur le parvis rouge dédié à 'Air France La Première'.
                    </strong>
                  </>
                )}
              </div>
              <div>N° de vol :</div>
              <div>{booking.flight_number || "N/A"}</div>
            </ItemsGrid>
          </Content>
          <Content>
            <h3>Informations complémentaires</h3>
            <ItemsGrid>
              <div>Véhicule :</div>
              <div>{booking.vehicle}</div>
              <div>Statut :</div>
              <div>
                {bookingProgressStatusList?.[booking.progress_status] || "N/A"}
              </div>
              {booking.driverJwt && (
                <>
                  <div>Commentaire fin de trajet :</div>
                  <div>
                    <form className="form-group" onSubmit={submitComment}>
                      <textarea
                        id="comment"
                        className="form-control col-12 col-md-9 col-lg-6"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                      ></textarea>
                      {(booking.driver_comment || "") !== (comment || "") && (
                        <input
                          className="btn btn-primary mt-2"
                          type="submit"
                          value="Envoyer"
                        />
                      )}
                    </form>
                  </div>
                </>
              )}
            </ItemsGrid>
          </Content>
          {booking.driverJwt ? (
            <UpdateStatusBtnContainer>
              <div className="btn-group">
                <button
                  className="btn bnt-sm btn-primary ml-3"
                  disabled={updating || booking.progress_status === 4}
                  onClick={updateStatus}
                >
                  {
                    bookingProgressStatusActionList[
                      booking.progress_status || 0
                    ]
                  }
                </button>
                <Dropdown className="btn btn-primary actions">
                  <Dropdown.Toggle>
                    <DotsIcon />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={driverLeave} disabled={updating}>
                      Annuler la course
                    </Dropdown.Item>
                    {availableDrivers && availableDrivers.length > 0 && (
                      <Dropdown.Item
                        disabled={updating}
                        onClick={(e) => setShowUpdateDriverModal(true)}
                      >
                        Changer de chauffeur
                      </Dropdown.Item>
                    )}
                    <Dropdown.Item onClick={updateStopList} disabled={updating}>
                      Signaler un arrêt
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={updateNoShow}
                      disabled={updating || booking.noShow}
                    >
                      {booking.noShow
                        ? '"No Show" signalé'
                        : 'Signaler un "No Show"'}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </UpdateStatusBtnContainer>
          ) : (
            <UpdateStatusBtnContainer>
              <div className="btn-group">
                <a
                  className="btn bnt-sm btn-primary ml-3"
                  href={`tel:${booking.driver_phone}`}
                >
                  Appeler le chauffeur
                </a>
              </div>
            </UpdateStatusBtnContainer>
          )}
          {booking.driverJwt && (
            <Content>
              <h3>Courses à venir</h3>
              <ComingList>
                {comingBookings.map((b) => (
                  <li key={b.id}>
                    <a
                      href={b.driverJwt}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <strong>{b.pickup_date.displayDate}</strong>
                      <div>Départ de {b.pickup_address}</div>
                      {b.flight_number && (
                        <div>
                          N° de vol : <em>{b.flight_number}</em>
                        </div>
                      )}
                    </a>
                  </li>
                ))}
              </ComingList>
            </Content>
          )}
        </>
      ) : (
        <div>
          <h3 className="text-center">Loading</h3>
        </div>
      )}
      <UpdateDriverModal
        driverOptions={availableDrivers}
        booking={bookingRaw}
        show={showUpdateDriverModal}
        setShow={setShowUpdateDriverModal}
        fetching={fetchingUpdateDriver}
        submit={updateDriver}
      />
    </div>
  );
}

// region styles

const Container = styled.div`
  width: 100%;
  max-width: 1100px;
  position: relative;
  z-index: 0;
  margin: 0 auto;
  margin-bottom: 2rem;
`;

const Content = styled(Container)`
  background-color: white;
  padding: 1rem;
  border-radius: 10px;

  h3 {
    font-size: 1.2rem;
    margin-bottom: 1rem;
  }
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 3rem;
  row-gap: 4px;

  form.form-group {
    margin-bottom: 0;
  }

  @media (max-width: 720px) {
    grid-template-columns: minmax(60px, 80px) 1fr;
    column-gap: 1rem;
    row-gap: 12px;
  }
`;

const UpdateStatusBtnContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;

  display: grid;
  place-items: center;

  background-color: white;
  padding: 1rem;
  z-index: 9999999;

  & > div {
    & > button, & > a {
      font-size: 2rem;
      width: auto;
      padding-left: 42px;
      padding-right: 42px;
    }

    .actions {
      display: grid;
      place-items: center;
      border: 1px solid var(--bs-primary);
      padding: 0;
      transition: all ease 300ms;
      background-color: transparent;

      svg {
        color: black;
        transition: all ease 300ms;
      }

      &.show,
      &:hover {
        border: 1px solid var(--bs-btn-hover-bg);
        background: var(--bs-btn-hover-bg);

        svg {
          color: white;
        }
      }

      button {
        width: 100%;
        height: 100%;
        background-color: transparent;
        border: none;
        padding: 9px 12px;
        display: grid;
        place-items: center;

        &:after {
          display: none;
        }

        &:active,
        &:focus,
        &.show {
          background-color: transparent !important;
          border-color: transparent !important;
          box-shadow: none;
        }
      }
    }
  }
`;

const ComingList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  row-gap: 1em;

  li {
    cursor: pointer;

    a {
      color: inherit;
    }
  }
`;

// endregion

export default TrackContent;
